<template>
  <div class="vs-main" style="padding: 0">
    <div class="vs-layout-split" style="grid-template-columns: 300px 1fr">
      <!-- Left Panel -->
      <div class="vs-filter-panel" style="border-left: none; border-right: 1px solid var(--aws-border); overflow-y: auto; max-height: calc(100vh - 56px)">
        <div class="vs-fp-title" style="padding: 10px 16px 8px; font-size: 14px">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" style="width:14px;height:14px"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4m0-4h.01"/></svg>
          Report parameters
        </div>

        <!-- Year -->
        <div class="vs-fp-group">
          <div class="vs-fp-label">Year<button class="vs-fp-clear" @click="form.year_mode = 'current'; onFilterChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.year_mode" @change="onFilterChange">
            <option v-for="y in yearOptions" :key="y.value" :value="y.value">{{ y.label }}</option>
          </select>
        </div>

        <!-- Bill Type -->
        <div class="vs-fp-group">
          <div class="vs-fp-label">Bill Type<button class="vs-fp-clear" @click="form.billType = 1; onFilterChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.billType" @change="onFilterChange">
            <option v-for="bt in billTypeOptions" :key="bt.value" :value="bt.value">{{ bt.name }}</option>
          </select>
        </div>

        <!-- Sales Rep -->
        <div class="vs-fp-group" v-if="userType === 1 || userType === 3">
          <div class="vs-fp-label">Sales Rep<button class="vs-fp-clear" @click="form.sales = ''; onFilterChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.sales" @change="onSalesChange">
            <option value="">All</option>
            <option v-for="s in salesOptions" :key="s">{{ s }}</option>
          </select>
        </div>

        <!-- Partner（伙伴/客户登录已隔离为自身，无需筛选，隐藏下拉） -->
        <div class="vs-fp-group" v-if="userType === 1 || userType === 3">
          <div class="vs-fp-label">Partner<button class="vs-fp-clear" @click="form.agent_id = ''; onAgentChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.agent_id" @change="onAgentChange">
            <option value="">All</option>
            <option v-for="a in filteredAgents" :key="a.agent_id" :value="a.agent_id">{{ a.company }} ({{ a.agent_id }})</option>
          </select>
        </div>

        <!-- Payer -->
        <div class="vs-fp-group">
          <div class="vs-fp-label">Payer<button v-if="!lockPayer" class="vs-fp-clear" @click="form.payer_id = ''; onPayerChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.payer_id" @change="onPayerChange" :disabled="lockPayer">
            <option value="">All</option>
            <option v-for="p in filteredPayers" :key="p.payer_id" :value="p.payer_id">{{ p.name }} ({{ p.payer_id }})</option>
          </select>
        </div>

        <!-- Link -->
        <div class="vs-fp-group">
          <div class="vs-fp-label">Link<button v-if="!lockLink" class="vs-fp-clear" @click="form.link_id = ''; onFilterChange()">Clear</button></div>
          <select class="vs-form-select" v-model="form.link_id" @change="onFilterChange" :disabled="lockLink">
            <option value="">All</option>
            <option v-for="l in filteredLinks" :key="l.link_id" :value="l.link_id">{{ l.name || l.link_id }} ({{ l.link_id }})</option>
          </select>
        </div>

        <!-- Entity Tree: Agent → Link -->
        <div class="vs-entity-tree">
          <div class="vs-et-label">EA Entity Tree<span class="vs-info-link" style="font-size: 12px" @click="collapseAll">Collapse all</span></div>
          <ul class="vs-et-list">
            <template v-for="agent in treeData" :key="agent.agent_id">
              <li class="vs-et-item" style="font-weight: 700; color: var(--aws-text-primary)" @click="toggleAgent(agent)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :style="{ transform: agent.expanded ? 'rotate(90deg)' : '' }"><path d="M9 5l7 7-7 7"/></svg>
                <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">{{ agent.company }}</span>
                <span class="vs-et-id">{{ agent.agent_id }}</span>
              </li>
              <template v-if="agent.expanded">
                <li v-for="link in agent.links" :key="link.link_id"
                    :class="['vs-et-item', selectedNode === link.link_id ? 'active' : '']"
                    style="padding-left: 28px"
                    @click="selectNode(link, agent)">
                  <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">{{ link.name }}</span>
                  <span class="vs-et-id">{{ link.link_id }}</span>
                </li>
              </template>
            </template>
          </ul>
        </div>
      </div>

      <!-- Main Content -->
      <div style="flex: 1; padding: 20px 28px 40px; overflow: auto">
        <!-- Cost and usage graph -->
        <div class="vs-section">
          <div class="vs-section-head">
            <div style="display: flex; align-items: center; gap: 8px">
              <span class="vs-section-title">Cost and usage graph</span>
              <span class="vs-info-link" @click="showInfo('cost_graph')">Info</span>
            </div>
            <div v-if="infoVisible === 'cost_graph'" class="vs-info-popup" style="right: 20px; top: 48px; left: auto">
              {{ infoTexts.cost_graph }}
              <span class="vs-info-close" @click="closeInfo">×</span>
            </div>
            <div class="vs-seg-control">
              <button :class="['vs-seg-btn', showCharge ? 'active' : '']" @click="toggleCharge">Charge</button>
              <button :class="['vs-seg-btn', showActualCost ? 'active' : '']" @click="toggleActualCost">Actual Cost</button>
            </div>
          </div>
          <div class="vs-section-body">
            <div class="vs-kpi-row">
              <div class="vs-kpi-item">
                <div class="vs-kpi-label">Total cost</div>
                <div class="vs-kpi-value" style="font-size: 24px">{{ fmt(kpi.total_charge) }}</div>
              </div>
              <div class="vs-kpi-item">
                <div class="vs-kpi-label">Average monthly cost</div>
                <div class="vs-kpi-value" style="font-size: 24px">{{ fmt(kpi.avg) }}</div>
              </div>
              <div class="vs-kpi-item">
                <div class="vs-kpi-label">Service count</div>
                <div class="vs-kpi-value" style="font-size: 24px">{{ kpi.svc }}</div>
              </div>
            </div>
            <div style="font-size: 14px; color: #545B64; margin-bottom: 8px">Costs ($)</div>
            <div class="vs-chart-container h320" ref="chartBarRef"></div>
            <div class="vs-chart-note">Click bar to view monthly bill details</div>
          </div>
        </div>

        <!-- Cost breakdown + Trend -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-bottom: 20px">
          <div class="vs-section" style="border-right: none; margin-bottom: 0">
            <div class="vs-section-head">
              <span class="vs-section-title">Cost breakdown by payer</span>
              <span class="vs-info-link" @click="showInfo('payer_breakdown')">Info</span>
              <div v-if="infoVisible === 'payer_breakdown'" class="vs-info-popup">
                {{ infoTexts.payer_breakdown }}
                <span class="vs-info-close" @click="closeInfo">×</span>
              </div>
            </div>
            <div class="vs-section-body">
              <div class="vs-kpi-row">
                <div class="vs-kpi-item">
                  <div class="vs-kpi-label">Monthly Charge</div>
                  <div class="vs-kpi-value" style="font-size: 22px">{{ fmt(kpiPayer.charge) }}</div>
                </div>
                <div class="vs-kpi-item">
                  <div class="vs-kpi-label">Monthly Actual Cost</div>
                  <div class="vs-kpi-value" style="font-size: 22px; color: #D13212">{{ fmt(kpiPayer.actual) }}</div>
                </div>
              </div>
              <div class="vs-chart-container h240" ref="chartPieRef"></div>
            </div>
          </div>
          <div class="vs-section" style="margin-bottom: 0">
            <div class="vs-section-head">
              <span class="vs-section-title">Cost trend</span>
              <span class="vs-info-link" @click="showInfo('cost_trend')">Info</span>
              <div v-if="infoVisible === 'cost_trend'" class="vs-info-popup">
                {{ infoTexts.cost_trend }}
                <span class="vs-info-close" @click="closeInfo">×</span>
              </div>
            </div>
            <div class="vs-section-body">
              <div class="vs-kpi-row">
                <div class="vs-kpi-item">
                  <div class="vs-kpi-label">Annual Charge</div>
                  <div class="vs-kpi-value" style="font-size: 22px">{{ fmt(kpiTrend.charge) }}</div>
                </div>
                <div class="vs-kpi-item">
                  <div class="vs-kpi-label">Annual Actual Cost</div>
                  <div class="vs-kpi-value" style="font-size: 22px; color: #D13212">{{ fmt(kpiTrend.actual) }}</div>
                </div>
              </div>
              <div class="vs-chart-container h240" ref="chartTrendRef"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'
import { ajaxGet } from '@/api/request'
import { useBillDataStore } from '@/store/billData'
import '@/assets/css/aws-console.scss'

const AWS_PALETTE = ['#6B9BD2', '#8C6BB1', '#2E5F99', '#EC7211', '#879596', '#067F68', '#D13212', '#00A1C9']
const router = useRouter()
const store = useBillDataStore()

const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
const userType = userInfo.type || 1
const isLimitedRole = computed(() => userType === 2 || userType === 4)

// Form state
const form = reactive({
  year_mode: 'current',
  billType: userType === 2 ? 2 : (userType === 4 ? 3 : 1),
  sales: '',
  agent_id: '',
  payer_id: '',
  link_id: '',
})
const costMode = ref('charge')
const selectedNode = ref('')

// Store monthly data for chart re-rendering on costMode toggle
const monthlyChartData = ref([])
const showCharge = ref(true)
const showActualCost = ref(true)

// Info tooltip state
const infoVisible = ref('')
const infoTexts = {
  cost_graph: '月度费用统计图表，展示 Charge（账单原价）和 Actual Cost（扣除折扣、抵扣后的实际花费）的月度对比。',
  payer_breakdown: '按 Payer 账户分组的费用占比分布，展示当月各 Payer 的 Charge 金额及占比。',
  cost_trend: '年度费用趋势图，展示 Charge 和 Actual Cost 的月度变化趋势，帮助识别费用走势。',
}

function showInfo(key) {
  infoVisible.value = infoVisible.value === key ? '' : key
}
function closeInfo() {
  infoVisible.value = ''
}

function toggleCharge() {
  showCharge.value = !showCharge.value
  updateBarChart()
}

function toggleActualCost() {
  showActualCost.value = !showActualCost.value
  updateBarChart()
}

function updateBarChart() {
  const barEl = chartBarRef.value
  if (!barEl) return
  const instance = echarts.getInstanceByDom(barEl)
  if (!instance) return

  const months = monthlyChartData.value
  instance.setOption({
    series: [
      { name: 'Charge', data: showCharge.value ? months.map(m => m.charge) : months.map(() => 0) },
      { name: 'Actual Cost', data: showActualCost.value ? months.map(m => m.actual_cost) : months.map(() => 0) },
    ]
  })
}

// Year options — loaded from DB
const yearOptions = ref([{ label: '实时', value: 'current' }])

async function loadYearOptions() {
  try {
    const res = await ajaxGet({ url: '/stat/analysis/available_years/' })
    const years = res?.data || res?.years || []
    yearOptions.value = [{ label: '实时', value: 'current' }]
    const cy = String(new Date().getFullYear())
    years.filter(y => String(y) !== cy).forEach(y => { yearOptions.value.push({ label: String(y), value: String(y) }) })
  } catch (e) {
    // Fallback: current year and last year
    const now = new Date().getFullYear()
    yearOptions.value = [
      { label: '实时', value: 'current' },
      { label: String(now - 1), value: String(now - 1) },
      { label: String(now - 2), value: String(now - 2) },
    ]
  }
}

// Bill type options by role
const billTypeOptions = computed(() => {
  if (userType === 2) return [{ name: '伙伴应付', value: 2 }, { name: '客户应付', value: 3 }]
  if (userType === 4) return [{ name: '客户应付', value: 3 }]
  return [{ name: '总代应付', value: 1 }, { name: '伙伴应付', value: 2 }, { name: '客户应付', value: 3 }]
})

// Cascading filter options
const salesOptions = computed(() => [...new Set((store.sales || []).map(s => s.name || s.realname || s).filter(Boolean))].sort())

const filteredAgents = computed(() => {
  let list = store.agents || []
  if (form.sales) list = list.filter(a => a.sales === form.sales)
  return list
})

const filteredPayers = computed(() => {
  let list = store.payers || []
  if (form.agent_id) {
    const agent = (store.agents || []).find(a => a.agent_id === form.agent_id)
    if (agent && agent.payers) {
      const ids = agent.payers.split(',').map(id => id.trim())
      list = list.filter(p => ids.includes(p.payer_id))
    }
  }
  return list
})

const filteredLinks = computed(() => {
  let list = store.links || []
  if (form.payer_id) list = list.filter(l => l.payer_id === form.payer_id)
  else if (form.agent_id) list = list.filter(l => l.agent_id === form.agent_id)
  return list.slice(0, 200)
})

const lockPayer = computed(() => isLimitedRole.value && filteredPayers.value.length <= 1)
const lockLink = computed(() => isLimitedRole.value && filteredLinks.value.length <= 1)

function initLimitedRoleFilters() {
  if (!isLimitedRole.value) return
  if (!form.payer_id && filteredPayers.value.length === 1) {
    form.payer_id = filteredPayers.value[0].payer_id
  }
  if (!form.link_id && filteredLinks.value.length === 1) {
    form.link_id = filteredLinks.value[0].link_id
  }
}

// ========== Entity Tree (reactive, not computed) ==========
const treeData = ref([])

function buildTree() {
  let agents = filteredAgents.value
  const links = store.links || []

  // If a specific agent is selected, only show that agent
  if (form.agent_id) {
    agents = agents.filter(a => a.agent_id === form.agent_id)
  }

  treeData.value = agents.map(a => {
    let agentLinks = links.filter(l => l.agent_id === a.agent_id)
    if (form.payer_id) agentLinks = agentLinks.filter(l => l.payer_id === form.payer_id)
    if (form.link_id) agentLinks = agentLinks.filter(l => l.link_id === form.link_id)

    return {
      agent_id: a.agent_id,
      company: a.company || a.agent_id,
      expanded: false,
      links: agentLinks.map(l => ({
        link_id: l.link_id,
        name: l.name || l.customer_name || l.link_id,
        payer_id: l.payer_id,
        agent_id: a.agent_id,
      }))
    }
  }).filter(a => a.links.length > 0)
}

function toggleAgent(agent) {
  agent.expanded = !agent.expanded
  // Load data for this agent
  selectedNode.value = ''
  form.agent_id = agent.agent_id
  form.link_id = ''
  loadCharts()
}

function collapseAll() {
  treeData.value.forEach(a => { a.expanded = false })
}

function selectNode(link, agent) {
  selectedNode.value = link.link_id
  // Update form and reload charts with this specific link
  form.link_id = link.link_id
  form.agent_id = agent.agent_id
  loadCharts()
}

// Filter change handlers
function onSalesChange() {
  form.agent_id = ''
  form.payer_id = ''
  form.link_id = ''
  selectedNode.value = ''
  buildTree()
  loadCharts()
}
function onAgentChange() {
  form.payer_id = ''
  form.link_id = ''
  selectedNode.value = ''
  buildTree()
  loadCharts()
}
function onPayerChange() {
  form.link_id = ''
  selectedNode.value = ''
  buildTree()
  loadCharts()
}
function onFilterChange() {
  selectedNode.value = ''
  buildTree()
  loadCharts()
}

// KPI state
const kpi = ref({ total_charge: 0, avg: 0, svc: 0 })
const kpiPayer = ref({ charge: 0, actual: 0 })
const kpiTrend = ref({ charge: 0, actual: 0 })

// Chart refs
const chartBarRef = ref(null)
const chartPieRef = ref(null)
const chartTrendRef = ref(null)
const charts = []

function fmt(v) {
  if (Math.abs(v) >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M'
  if (Math.abs(v) >= 1e3) return '$' + (v / 1e3).toFixed(2) + 'K'
  return '$' + (v || 0).toFixed(2)
}
function fmtAxis(v) {
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M'
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(0) + 'K'
  return v.toFixed(0)
}

function initChart(el, option) {
  if (!el) return null
  const c = echarts.init(el)
  c.setOption(option)
  charts.push(c)
  return c
}

function buildParams() {
  const p = { billType: form.billType }
  // Year: 'current' → current year, otherwise specific year
  if (form.year_mode === 'current') {
    p.year_mode = 'current'
  } else {
    p.year_mode = form.year_mode  // specific year like '2025'
  }
  if (form.agent_id) p.agent_id = form.agent_id
  if (form.payer_id) p.payer_id = form.payer_id
  if (form.link_id) p.link_id = form.link_id
  return p
}

async function loadCharts() {
  charts.forEach(c => c.dispose())
  charts.length = 0
  await nextTick()

  const params = buildParams()

  try {
    const costRes = await ajaxGet({ url: '/stat/analysis/cost_graph/', params })
    const costData = costRes || {}
    const months = costData.months || []
    monthlyChartData.value = months
    kpi.value = { total_charge: costData.total_actual_cost || 0, avg: (costData.total_actual_cost || 0) / (months.length || 1), svc: costData.service_count || 0 }

    const latestMonth = months.length > 0 ? months[months.length - 1].month : ''
    const payerParams = { ...params }
    if (latestMonth) payerParams.month = latestMonth

    const [payerRes, trendRes] = await Promise.all([
      ajaxGet({ url: '/stat/analysis/payer_breakdown/', params: payerParams }),
      ajaxGet({ url: '/stat/analysis/cost_trend/', params }),
    ])

    // Bar chart
    const barChart = initChart(chartBarRef.value, {
      grid: { left: 60, right: 20, top: 30, bottom: 50 },
      legend: { top: 0, textStyle: { color: '#545B64', fontSize: 12 } },
      xAxis: { type: 'category', data: months.map(m => m.month), axisLine: { lineStyle: { color: '#EAEDED' } }, axisLabel: { color: '#545B64' } },
      yAxis: { type: 'value', axisLabel: { color: '#545B64', formatter: fmtAxis }, splitLine: { lineStyle: { color: '#EAEDED' } } },
      series: [
        { name: 'Charge', type: 'bar', data: months.map(m => m.charge), barWidth: '30%', itemStyle: { color: '#6B9BD2' } },
        { name: 'Actual Cost', type: 'bar', data: months.map(m => m.actual_cost), barWidth: '30%', itemStyle: { color: '#EC7211' } },
      ],
      tooltip: { trigger: 'axis', backgroundColor: '#16191F', borderColor: '#16191F', textStyle: { color: '#fff' } },
    })
    if (barChart) {
      barChart.on('click', (p) => {
        const m = months[p.dataIndex]?.month
        if (m) router.push({ path: '/billDetailView', query: { period: m.substring(0, 4) + '-' + m.substring(4), ...params } })
      })
    }

    // Payer pie
    const payerData = payerRes?.data || []
    kpiPayer.value = { charge: payerData.reduce((s, r) => s + (r.charge || 0), 0), actual: payerData.reduce((s, r) => s + (r.actual_cost || 0), 0) }
    initChart(chartPieRef.value, {
      legend: { bottom: 0, textStyle: { color: '#545B64', fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
      series: [{ type: 'pie', radius: ['55%', '75%'], center: ['50%', '40%'], label: { show: false },
        data: payerData.map((r, i) => ({ value: r.charge, name: `${r.payer_name} (${r.share}%)`, itemStyle: { color: AWS_PALETTE[i % AWS_PALETTE.length] } })),
      }],
      tooltip: { trigger: 'item', backgroundColor: '#16191F', borderColor: '#16191F', textStyle: { color: '#fff' } },
    })

    // Trend line
    const trendData = trendRes || {}
    const trendMonths = trendData.months || []
    kpiTrend.value = { charge: trendData.annual_charge || 0, actual: trendData.annual_actual_cost || 0 }
    initChart(chartTrendRef.value, {
      grid: { left: 60, right: 20, top: 30, bottom: 50 },
      legend: { top: 0, textStyle: { color: '#545B64', fontSize: 12 } },
      xAxis: { type: 'category', data: trendMonths.map(m => m.month), axisLine: { lineStyle: { color: '#EAEDED' } }, axisLabel: { color: '#545B64' } },
      yAxis: { type: 'value', axisLabel: { color: '#545B64', formatter: fmtAxis }, splitLine: { lineStyle: { color: '#EAEDED' } } },
      series: [
        { name: 'Charge', type: 'line', data: trendMonths.map(m => m.charge), lineStyle: { color: '#6B9BD2', width: 2 }, itemStyle: { color: '#6B9BD2' }, symbol: 'circle', symbolSize: 8, smooth: true },
        { name: 'Actual Cost', type: 'line', data: trendMonths.map(m => m.actual_cost), lineStyle: { color: '#EC7211', width: 2 }, itemStyle: { color: '#EC7211' }, symbol: 'circle', symbolSize: 8, smooth: true },
      ],
      tooltip: { trigger: 'axis', backgroundColor: '#16191F', borderColor: '#16191F', textStyle: { color: '#fff' } },
    })
  } catch (e) {
    console.error('BillAnalysis loadCharts error:', e)
  }
}

function handleResize() { charts.forEach(c => c.resize()) }

onMounted(async () => {
  if (!store.loaded) await store.loadData()
  await loadYearOptions()
  initLimitedRoleFilters()
  buildTree()
  await nextTick()
  await loadCharts()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  charts.forEach(c => c.dispose())
})
</script>

<style lang="scss" scoped>
.vs-main { margin: 0; padding: 0; min-height: 100%; }
/* Compact filter groups */
.vs-filter-panel .vs-fp-group { padding: 4px 16px; }
.vs-filter-panel .vs-fp-label { font-size: 12px; margin-bottom: 2px; }
.vs-filter-panel .vs-form-select { padding: 3px 32px 3px 10px; font-size: 13px; height: 28px; }
/* Info popup tooltip — AWS white style */
.vs-info-popup {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  background: #FFFFFF;
  color: #16191F;
  font-size: 13px;
  font-weight: 400;
  padding: 12px 16px;
  border: 1px solid #D5DBDB;
  border-radius: 2px;
  max-width: 320px;
  line-height: 1.5;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  margin-top: 4px;
}
.vs-info-close {
  position: absolute;
  top: 4px;
  right: 8px;
  cursor: pointer;
  font-size: 16px;
  color: #AAB7B8;
  &:hover { color: #16191F; }
}
/* Make section-head relative for popup positioning */
.vs-section-head { position: relative; }
.vs-section-head > div { position: relative; }
</style>
<style>
.index-main:has(.vs-layout-split) { padding: 0 !important; }
</style>
